import { FC, RefObject, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { type PaginationProps, Divider, Flex } from 'antd';
import { Table } from '@tanstack/react-table';
import { token } from '@atlas-frontend/design-tokens';
import {
  type ApiErrorResponse,
  Bleed,
  BookmarkCreate,
  CustomPagination,
  DetailLayout,
  defaultSampleFormData,
  DraftAutosaver,
  FORM_SUBMIT_NOT_COMMITTED,
  Form,
  FormAlerts,
  formatDate,
  FormRef,
  FormSubmitHandler,
  GetApiErrorMessage,
  getExcelErrorMessage,
  HLock,
  LastChangedEntity,
  Padding,
  PermissionCode,
  PopconfirmDelete,
  SampleDataSubjectFormDataType,
  SamplePageType,
  SampleSchema,
  SampleValidFormDataType,
  TotalEntries,
  useAlertContext,
  useDeleteAllSamplesMutation,
  useMutationErrors,
  InfoIconUrl,
  EditSessionFormRowsContextProvider,
  useEditSessionContext,
  useSyncFormToNewLoadedData,
  useEditSessionFormRowsContext,
  type TableProps,
  ValidationMessagesMap,
} from '@atlas-frontend/sdk';
import { SectionBreadcrumbs } from '../../common/SectionBreadcrumbs';
import { SampleActions } from './SampleActions';
import { SampleDataSubjectTable } from './SampleDataSubjectTable';
import { SampleHeader } from './SampleHeader';
import { SAMPLE_ROUTES } from './Samples';
import { useSampleContext } from './state/SampleContext';
import { useSampleDetailsContext } from './state/SampleDetailsContext';
import { useHasPermissionToManageSample } from './useHasPermissionToManageSample';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './SampleDetailForm.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory('sample-detail');
addTranslations(translations);

export interface SampleDetailFormProps {
  pageData: SamplePageType;
  paginationProps: PaginationProps;
  tableProps: Pick<
    TableProps<SampleDataSubjectFormDataType>,
    'onFilter' | 'onSort' | 'filters' | 'sort'
  >;
  isLoading: boolean;
  onReload: () => Promise<void>;
}

export const SampleDetailForm: FC<SampleDetailFormProps> = (props) => {
  const { pageData, paginationProps, tableProps, isLoading, onReload } = props;
  const { t } = useTranslation();
  const translateMutationError = useCallback<GetApiErrorMessage>(
    (code, meta) =>
      t(`alert.error.code.${code}`, meta?.args && { ...meta.args }),
    [t],
  );

  const {
    groupOrDescriptionId,
    groupOrDescription,
    date,
    sampleType,
    selectedGroupMemberId,
  } = useSampleContext();

  const { treeData, viewType, setViewType, isEditing } =
    useSampleDetailsContext();

  const { updateRow, commitEdits } = useEditSessionContext();

  const ref: RefObject<FormRef<any> | null> = useRef(null);

  const title = useMemo(
    () =>
      `${groupOrDescription?.code} - ${groupOrDescription?.name} - ${formatDate(
        date,
      )}`,
    [date, groupOrDescription],
  );

  const {
    mutationErrors: newSampleValidationErrors,
    setMutationErrors: setNewSampleValidationErrors,
    clearMutationErrors: clearNewSampleValidationErrors,
  } = useMutationErrors(translateMutationError);

  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(getExcelErrorMessage);

  const handleMutationError = useCallback(
    (error: unknown) => {
      const { response } = error as ApiErrorResponse;
      setMutationErrors(response?.data.errors ?? [], translateMutationError);
    },
    [setMutationErrors, translateMutationError],
  );

  const serverErrors = useMemo(
    () =>
      new ValidationMessagesMap()
        .merge(newSampleValidationErrors)
        .merge(mutationErrors),
    [newSampleValidationErrors, mutationErrors],
  );

  const { resetAlert, setSuccess } = useAlertContext();

  const { mutateAsync: deleteAllAsync, isPending: isDeleteAllPending } =
    useDeleteAllSamplesMutation();

  const handleDeleteAll = useCallback(async () => {
    resetAlert();
    clearMutationErrors();

    try {
      await deleteAllAsync({
        id: groupOrDescriptionId,
        valueDate: date,
        type: sampleType,
      });
    } catch (error) {
      handleMutationError(error);
      return;
    }

    setSuccess();
    await onReload();
  }, [
    resetAlert,
    clearMutationErrors,
    groupOrDescriptionId,
    date,
    sampleType,
    deleteAllAsync,
    setSuccess,
    onReload,
    handleMutationError,
  ]);

  const handleSubmit: FormSubmitHandler<SampleValidFormDataType> = async () => {
    resetAlert();
    clearMutationErrors();

    try {
      if ((await commitEdits()) === 'blocked') {
        return FORM_SUBMIT_NOT_COMMITTED;
      }

      setSuccess();
      ref.current?.supressBlock();
    } catch (error) {
      handleMutationError(error);
      return FORM_SUBMIT_NOT_COMMITTED;
    }
  };

  const handleAbort = () => {
    setViewType('detail');
  };

  const lastChangedSample = useMemo(
    () =>
      pageData?.content
        .filter((s) => !!s.modifiedAt)
        .sort((a, b) => (b as any).modifiedAt - (a as any).modifiedAt)[0] ||
      null,
    [pageData],
  );

  const hasPermissionToRemoveFromSample = useHasPermissionToManageSample(
    groupOrDescriptionId,
    sampleType,
    PermissionCode.SAMPLE_SUBJECT_REMOVE,
  );

  const dropdownItems = useMemo(
    () =>
      hasPermissionToRemoveFromSample
        ? [
            {
              key: 'deleteAll',
              label: (
                <PopconfirmDelete
                  stopClickPropagation
                  disabled={isDeleteAllPending}
                  onDelete={handleDeleteAll}
                  genitiveModelName={t('delete.genetive')}
                >
                  {t('buttons.deleteAll')}
                </PopconfirmDelete>
              ),
            },
          ]
        : [],
    [t, hasPermissionToRemoveFromSample, isDeleteAllPending, handleDeleteAll],
  );

  const tableRef = useRef<Table<SampleDataSubjectFormDataType> | null>(null);

  useSyncFormToNewLoadedData(pageData, ref);

  return (
    <Form
      schema={SampleSchema.validFormDataSchema}
      defaultValues={{ samples: pageData?.content ?? [] }}
      onSubmit={handleSubmit}
      serverErrors={serverErrors}
      formRef={ref}
      blockNavigationOnFirstChange={isEditing}
    >
      <EditSessionFormRowsContextProvider
        fieldArrayName="samples"
        defaultRow={defaultSampleFormData}
        pagination={paginationProps}
        pageData={pageData}
        onValidationErrors={setNewSampleValidationErrors}
        onValidationErrorsResolved={clearNewSampleValidationErrors}
        onOperationError={handleMutationError}
      >
        {isEditing && (
          <DraftAutosaver fieldArrayName="samples" updateRow={updateRow} />
        )}
        <DetailLayoutWrapper
          infoIconUrl={InfoIconUrl.SAMPLE_DETAILS}
          breadCrumb={
            <SectionBreadcrumbs data={title} routes={SAMPLE_ROUTES} />
          }
          bookmarkCreate={
            <BookmarkCreate
              excludeUrlParams={['date']}
              includeUrlParams={[
                { key: 'periodSkipLastValueDate', value: 'true' },
              ]}
            />
          }
          headerConfig={{
            actions: (
              <SampleActions
                id={groupOrDescriptionId}
                onError={setMutationErrors}
                valueDate={date}
                sampleType={sampleType}
                tableRef={tableRef}
              />
            ),
            dropdownMenu:
              viewType === 'detail' && hasPermissionToRemoveFromSample
                ? {
                    items: dropdownItems,
                  }
                : undefined,
          }}
          alert={<FormAlerts />}
          footerContent={
            <>
              <FooterLeft>
                {viewType === 'mutate' ? (
                  <HLock
                    path={[groupOrDescription!.code, date]}
                    onLocked={handleAbort}
                  />
                ) : (
                  <LastChangedEntity entity={lastChangedSample ?? undefined} />
                )}
              </FooterLeft>
              <FooterWrapper>
                <TotalEntries count={pageData?.totalElements} />
                {pageData && <SampleFormPagination {...paginationProps} />}
              </FooterWrapper>
            </>
          }
        >
          <HeaderWrapper horizontal top>
            <SampleHeader
              groupMemberId={selectedGroupMemberId}
              id={groupOrDescriptionId}
              date={date}
              data={treeData}
              sampleType={sampleType}
              view="samples"
            />
            <Bleed direction="horizontal">
              <Divider />
            </Bleed>
          </HeaderWrapper>
          <ContentWrapper>
            <Flex vertical gap="middle" align="end">
              {sampleType === 'group' ? (
                <SampleDataSubjectTable
                  ref={tableRef}
                  isMutating={viewType === 'mutate'}
                  isLoading={isLoading}
                  groupId={groupOrDescriptionId}
                  descriptionId={selectedGroupMemberId}
                  {...tableProps}
                />
              ) : (
                <SampleDataSubjectTable
                  ref={tableRef}
                  isMutating={viewType === 'mutate'}
                  isLoading={isLoading}
                  descriptionId={groupOrDescriptionId}
                  {...tableProps}
                />
              )}
            </Flex>
          </ContentWrapper>
        </DetailLayoutWrapper>
      </EditSessionFormRowsContextProvider>
    </Form>
  );
};

const SampleFormPagination: FC<PaginationProps> = (props) => {
  const { onChange } = props;
  const { preparePendingRow } = useEditSessionFormRowsContext();

  // Wait for any pending row to be prepared before changing the page
  const handleChange: NonNullable<PaginationProps['onChange']> = useCallback(
    async (page, pageSize) => {
      if ((await preparePendingRow()) === 'ready') {
        onChange?.(page, pageSize);
      }
    },
    [preparePendingRow, onChange],
  );

  return <CustomPagination {...props} onChange={handleChange} />;
};

const FooterLeft = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  width: 418px;
  border-right: 1px solid ${token.colorBorderSecondary};
  margin-right: 32px;
`;

const FooterWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-right: 20px;
  width: 100%;
`;

const HeaderWrapper = styled(Padding)`
  position: sticky;
  left: 50%;
  transform: translateX(-50%);
  margin-inline: -999999px;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const ContentWrapper = styled(Padding)`
  width: 100%;
  min-width: fit-content;
  min-height: fit-content;
`;

const DetailLayoutWrapper = styled(DetailLayout)`
  overflow-x: hidden;
`;
